import axios from 'axios';
import { WizardData } from '../context/WizardContext';
import { AdvisorInfo, DEFAULT_CUSTOMER_ID } from '../types/advisor';

// API configuration
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Configure axios client for backend API
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
});

/**
 * Submit wizard data to the backend API
 * @param wizardData The completed wizard data
 * @returns Promise with the response
 */
export const submitWizardData = async (wizardData: WizardData): Promise<any> => {
  try {
    // Log API endpoints for debugging
    // console.log('API Endpoints:', {
    //   baseURL: API_URL,
    //   taxAlphaURL: TAXALPHA_API_URL
    // });
    
    // For demo purposes, we're just logging the data
    // console.log('Submitting wizard data:', wizardData);
    
    // Simulate API call with a delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    return {
      success: true,
      message: 'Data submitted successfully',
    };
  } catch (error) {
    console.error('Error submitting data:', error);
    throw new Error('Failed to submit data. Please try again.');
  }
};

// Helper function to format currency
function formatCurrency(value: number | undefined) {
  if (!value) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

/**
 * Helper function to convert goals object to API array
 */
const createGoalsArray = (goalsInfo: any) => {
  const goalsArray = [];
  
  if (goalsInfo.selectedGoals.financialIndependence) {
    goalsArray.push({ name: "Financial Independence" });
  }
  
  if (goalsInfo.selectedGoals.travel) {
    goalsArray.push({ name: "Travel & Adventure" });
  }
  
  if (goalsInfo.selectedGoals.legacy) {
    goalsArray.push({ name: "Leave a Legacy" });
  }
  
  if (goalsInfo.selectedGoals.healthcare) {
    goalsArray.push({ name: "Healthcare Security" });
  }
  
  if (goalsInfo.selectedGoals.lifestyle) {
    goalsArray.push({ name: "Lifestyle Maintenance" });
  }
  
  if (goalsInfo.selectedGoals.other && goalsInfo.otherGoalDescription) {
    goalsArray.push({ name: "Other", description: goalsInfo.otherGoalDescription });
  }
  
  return goalsArray;
}

/**
 * Submit data to automation API via backend
 * @param wizardData The completed wizard data
 * @param advisor Optional advisor info (not used anymore, kept for compatibility)
 * @returns Promise with the response
 */
export const submitToTaxAlpha = async (wizardData: WizardData, advisor?: AdvisorInfo | null): Promise<any> => {
  try {
    console.log('Submitting to automation API via backend:', wizardData.customerId);

    // STEP 1: Create plan request first (to enable callbacks from OutputGenerator)
    let planRequestId: string | null = null;
    let healthSpanResult: any = null; // qualitative themes for the end screen (basic-healthspan only)
    try {
      console.log('Creating plan request...');
      const planRequestResponse = await apiClient.post('/plan-requests/webform', {
        customerId: wizardData.customerId || DEFAULT_CUSTOMER_ID,
        formData: wizardData,
        sessionId: wizardData.sessionId || null
      });

      planRequestId = planRequestResponse.data?.id || null;
      healthSpanResult = planRequestResponse.data?.healthSpanResult || null;
      console.log('Plan request created:', planRequestId);
    } catch (planRequestError: any) {
      console.error('Failed to create plan request (continuing anyway):', {
        message: planRequestError.message,
        status: planRequestError.response?.status
      });
      // Continue without planRequestId - backward compatible
    }

    // STEP 2: Call automation API with planRequestId
    try {
      // Call backend automation endpoint (handles transformation and API key)
      const response = await apiClient.post('/automation/create-case', {
        customerId: wizardData.customerId || DEFAULT_CUSTOMER_ID,
        formData: wizardData,
        planRequestId: planRequestId // Include planRequestId to enable callbacks
      });

      console.log('Automation API Response: caseId=', response.data?.caseId || response.data?.data?.caseId);

      // Extract case ID from response
      const caseId = response.data?.caseId || response.data?.data?.caseId || null;

      // Intake notifications (client/advisor/support) are sent server-side
      // from /api/plan-requests/webform — no email work needed here.

      return {
        success: true,
        message: 'Data submitted successfully',
        caseId: caseId,
        planRequestId: planRequestId,
        healthSpanResult: healthSpanResult
      };
    } catch (apiError: any) {
      console.error('Automation API Error:', {
        message: apiError.message,
        status: apiError.response?.status,
        statusText: apiError.response?.statusText
      });

      // Continue execution even if automation fails. Intake emails were
      // already fired server-side when the plan request was created.
      return {
        success: true,
        message: 'Data submitted successfully',
        caseId: null,
        planRequestId: planRequestId,
        healthSpanResult: healthSpanResult
      };
    }
  } catch (error) {
    console.error('Error submitting data:', error);
    throw new Error('Failed to submit data. Please try again.');
  }
};

// Function to load customer data by ID (can be implemented when backend is ready)
export const loadCustomerData = async (customerId: number) => {
  try {
    const response = await apiClient.get(`/customers/${customerId}`);
    return response.data;
  } catch (error) {
    console.error(`Error loading customer data for ID ${customerId}:`, error);
    throw error;
  }
};
